import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { Storage } from '@ionic/storage-angular';
import { ReplaySubject, Subscription, combineLatest, Observable } from 'rxjs';

export enum ThemeMode {
  Dark = 'dark',
  Light = 'light',
  System = 'system',
}

const overrideColorSchemeStorageKey = 'OverrideColorScheme';

@Injectable({
  providedIn: 'root',
})
export class DarkModeService {
  private rendererFactory2 = inject(RendererFactory2);
  private readonly storage = inject(Storage);

  private _themeMode$ = new ReplaySubject<ThemeMode>(1);
  public get themeMode$(): Observable<ThemeMode> {
    return this._themeMode$.asObservable();
  }
  private _isDarkTheme$ = new ReplaySubject<boolean>(1);
  public get isDarkTheme$(): Observable<boolean> {
    return this._isDarkTheme$.asObservable();
  }
  private _isLightTheme$ = new ReplaySubject<boolean>(1);
  public get isLightTheme(): Observable<boolean> {
    return this._isLightTheme$.asObservable();
  }

  private reactToColorSchemeChangesSub!: Subscription;
  private preferredColorScheme$ = new ReplaySubject<ThemeMode>(1);
  private _overrideColorScheme$ = new ReplaySubject<ThemeMode>(1);
  public get overrideColorScheme(): Observable<ThemeMode> {
    return this._overrideColorScheme$.asObservable();
  }

  private unlistenPreferredColorSchemeChanges!: () => void;

  private readonly preferredMediaQuery: string = '(prefers-color-scheme: dark)';
  private readonly darkModeClass: string = 'dark';

  private renderer2: Renderer2;

  constructor() {
    const rendererFactory2 = this.rendererFactory2;

    this.renderer2 = rendererFactory2.createRenderer(null, null);
  }

  public async initTheming(): Promise<void> {
    this.detectPreferredColorSchemeChanges();
    this.reactToColorSchemeChanges();

    const preferred = window.matchMedia(this.preferredMediaQuery);
    const initialTheme = preferred.matches ? ThemeMode.Dark : ThemeMode.Light;

    this.preferredColorScheme$.next(initialTheme);

    const storedOverride: ThemeMode = await this.storage.get(
      overrideColorSchemeStorageKey,
    );

    if (
      storedOverride &&
      (storedOverride === ThemeMode.Light || storedOverride === ThemeMode.Dark)
    ) {
      this._overrideColorScheme$.next(storedOverride);
    } else {
      this._overrideColorScheme$.next(ThemeMode.System);
    }
  }

  public destroyTheming(): void {
    this.unlistenPreferredColorSchemeChanges();
    this._themeMode$.complete();
    this._isDarkTheme$.complete();
    this._isLightTheme$.complete();
    this.preferredColorScheme$.complete();
    this._overrideColorScheme$.complete();
    this.reactToColorSchemeChangesSub?.unsubscribe();
  }

  public setTheme(newThemeMode: ThemeMode): void {
    this._overrideColorScheme$.next(newThemeMode);
    this.storage.set(overrideColorSchemeStorageKey, newThemeMode);
  }

  private detectPreferredColorSchemeChanges(): void {
    this.unlistenPreferredColorSchemeChanges = this.renderer2.listen(
      window.matchMedia('(prefers-color-scheme: dark)'),
      'change',
      (event) => {
        this.preferredColorScheme$.next(
          event.matches ? ThemeMode.Dark : ThemeMode.Light,
        );
      },
    );
  }

  private reactToColorSchemeChanges(): void {
    this.reactToColorSchemeChangesSub = combineLatest([
      this.preferredColorScheme$,
      this._overrideColorScheme$,
    ]).subscribe(([preferredColorScheme, overrideColorScheme]) => {
      let newThemeMode: ThemeMode = ThemeMode.Light;
      if (overrideColorScheme && overrideColorScheme !== ThemeMode.System) {
        newThemeMode = overrideColorScheme;
      } else if (preferredColorScheme) {
        newThemeMode = preferredColorScheme;
      }

      this.toggleDarkModeClass(newThemeMode);
      this.toggleAndroidEdgeToEdgeBackgroundColor(newThemeMode);
      this._themeMode$.next(newThemeMode);
      this._isDarkTheme$.next(newThemeMode === ThemeMode.Dark ? true : false);
      this._isLightTheme$.next(newThemeMode === ThemeMode.Light ? true : false);
    });
  }

  private toggleDarkModeClass(theme: ThemeMode): void {
    document.body.classList.toggle(
      this.darkModeClass,
      theme === ThemeMode.Dark ? true : false,
    );
  }

  private async toggleAndroidEdgeToEdgeBackgroundColor(
    theme: ThemeMode,
  ): Promise<void> {
    if (
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === 'android' &&
      theme === ThemeMode.Dark
    ) {
      if (theme === ThemeMode.Dark) {
        await EdgeToEdge.setBackgroundColor({ color: '#000000' });
        await StatusBar.setStyle({ style: Style.Dark });
      } else {
        await EdgeToEdge.setBackgroundColor({ color: '#ffffff' });
        await StatusBar.setStyle({ style: Style.Light });
      }
    }
  }
}
